'use client'

import Cards from '@/ui/assets/components/cards.inline.svg'
import { Button } from '@/ui/atoms/button/Button'
import { Panel } from '@/ui/atoms/panel/Panel'
import { Typography } from '@/ui/atoms/typography/Typography'
import { AlertTriangle, ArrowRight, RefreshCw, Send } from 'lucide-react'
import { KYCStatus } from '../types/CardState'

interface CardKYCIssueProps {
  kycStatus?: KYCStatus
  onContactSupport: () => void
  onRetryKYC?: () => void
}

export function CardKYCIssue({ kycStatus, onContactSupport, onRetryKYC }: CardKYCIssueProps) {
  const getStatusMessage = () => {
    switch (kycStatus) {
      case KYCStatus.RESUBMIT:
        return 'Your KYC application requires resubmission. There may be issues with the submitted documents or information that need to be addressed.'
      case KYCStatus.ONHOLD:
        return 'Your KYC application has been placed on hold. This typically requires manual review or intervention from our team.'
      default:
        return 'There is an issue with your KYC application that requires attention from our team.'
    }
  }

  const getStatusTitle = () => {
    switch (kycStatus) {
      case KYCStatus.RESUBMIT:
        return 'KYC Resubmission Required'
      case KYCStatus.ONHOLD:
        return 'KYC Application On Hold'
      default:
        return 'KYC Issue Detected'
    }
  }

  const getStatusIcon = () => {
    switch (kycStatus) {
      case KYCStatus.RESUBMIT:
        return <RefreshCw className="h-6 w-6 text-yellow-400" />
      case KYCStatus.ONHOLD:
        return <AlertTriangle className="h-6 w-6 text-yellow-400" />
      default:
        return <AlertTriangle className="h-6 w-6 text-yellow-400" />
    }
  }

  const getActionMessage = () => {
    switch (kycStatus) {
      case KYCStatus.RESUBMIT:
        return 'Please review the feedback and resubmit your documents. You can retry the verification process below.'
      case KYCStatus.ONHOLD:
        return 'Our team will review your case and reach out with next steps. You can also contact us for more information.'
      default:
        return 'Please contact the HypurrFi team to resolve this issue and proceed with your application.'
    }
  }

  return (
    <div className="flex flex-col items-center gap-12 lg:flex-row lg:items-start">
      <div className="flex flex-1 flex-col gap-6">
        <Panel.Wrapper className="relative w-full max-w-xl overflow-hidden">
          <div className="pointer-events-none absolute inset-0 rounded-lg bg-gradient-to-br from-yellow-500/10 via-transparent to-yellow-400/5" />

          <Panel.Content className="relative flex flex-col gap-6 p-8">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-yellow-500/30 bg-yellow-500/10">
                {getStatusIcon()}
              </div>
              <div>
                <Typography variant="h3" className="text-yellow-400">
                  {getStatusTitle()}
                </Typography>
              </div>
            </div>

            <Typography variant="p" className="text-muted-foreground">
              {getStatusMessage()}
            </Typography>

            <div className="rounded-lg border border-yellow-500/20 bg-yellow-500/10 p-4">
              <Typography variant="p" className="text-sm text-yellow-400">
                <strong>Action Required:</strong> {getActionMessage()}
              </Typography>
            </div>

            <Typography variant="p" className="text-muted-foreground text-sm">
              {kycStatus === KYCStatus.RESUBMIT
                ? 'You can retry the verification or contact our team for assistance.'
                : 'Our team is available to help resolve this issue quickly.'}
            </Typography>

            <div className="flex flex-col gap-3">
              {kycStatus === KYCStatus.RESUBMIT && onRetryKYC && (
                <Button
                  onClick={onRetryKYC}
                  size="lg"
                  className="w-full transition-all duration-300 hover:opacity-90 hover:shadow-lg"
                  postfixIcon={<ArrowRight className="h-4 w-4" />}
                >
                  Retry Verification
                </Button>
              )}
              <Button
                onClick={onContactSupport}
                size="lg"
                variant={kycStatus === KYCStatus.RESUBMIT ? 'outline' : 'primary'}
                className="w-full transition-all duration-300 hover:opacity-90 hover:shadow-lg"
                prefixIcon={<Send className="h-4 w-4" />}
              >
                Contact HypurrFi Team
              </Button>
            </div>
          </Panel.Content>
        </Panel.Wrapper>
      </div>

      <div className="relative flex items-center justify-center lg:flex-1">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="h-[250px] w-[350px] rounded-full bg-gradient-to-br from-yellow-500/10 to-yellow-400/10 blur-[60px]" />
        </div>
        <div className="relative opacity-70 transition-opacity duration-500 hover:opacity-90">
          <Cards className="h-auto w-full max-w-sm" />
        </div>
      </div>
    </div>
  )
}
